import { PureComponent } from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import breakpointPropTypes from 'core/utils/prop-types/breakpoint';
import isNotEmptyReactElementContent from 'core/utils/content';

import BaseDrum from './BaseDrum';
import EmptyWrapper from 'core/components/EmptyWrapper';
import Link from 'core/components/Link';
import H3 from 'core/components/H3';
import Button from 'core/components/Button';
import Scroller from 'core/components/Scroller';

import styles from './index.styl';

/**
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 */
class Drum extends PureComponent {
  static propTypes = {
    /**
     * Данные о топиках.
     */
    content: PropTypes.array,

    /**
     * Катрочки для слайдера.
     */
    card: PropTypes.func.isRequired,

    /**
     * Заголовок
     */
    title: PropTypes.node,

    /**
     * @ignore
     */
    isMobile: PropTypes.bool,

    /**
     * @ignore
     */
    breakpoint: breakpointPropTypes(),

    /**
     * Расстояние между карточками в драме.
     *
     * Также, фоллбек для отступа от заголовка до слайдера.
     */
    spaceBetween: PropTypes.number,

    /**
     * Ограничение количества загружаемых топиков
     */
    limit: PropTypes.number,

    /**
     * Отступ от заголовка до слайдера.
     *
     * Если не указан, отработает fallback на spaceBetween
     */
    headerIndent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /**
     * Компонент, в который заварачивается заголовок.
     */
    headerComponent: PropTypes.func,

    /**
     * Ширина карточки. Может быть задана числом или в процентах.
     *
     * Игнорируется на мобильном лейауте.
     */
    itemWidth: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),

    /**
     * Ширина карточки на мобильном лейауте
     */
    itemWidthMobile: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),

    /**
     * Табы
     */
    tabs: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
      rubric: PropTypes.string,
    })),

    /**
     * Ссылка на все материалы. Отображается последним слайдом.
     * Также может располагаться под слайдером или рядом с заголовком если задан linkPosition.
     */
    link: PropTypes.string,

    /**
     * Положение ссылки на все материалы.
     *
     * Если есть табы, то ссылки не должно быть рядом с заголовком, поэтому
     * если переданы tabs и linkPosition='top', ссылка все равно будет снизу слева.
     *
     * На мобильной версии не отображается.
     */
    linkPosition: PropTypes.oneOf([
      'top',
      'bottom-left',
      'bottom-center',
    ]),

    /**
     * Текст ссылки
     */
    linkTitle: PropTypes.node,
    /**
     * Отключает bullets и счётчик слайдов на мобилках
     */
    noCounter: PropTypes.bool,

    cardsReplacement: PropTypes.object,

    /** @ignore */
    dataQa: PropTypes.string,

    /** autoHeight для swiper */
    autoHeight: PropTypes.bool,
  };

  static defaultProps = {
    spaceBetween: 20,
    limit: 6,
    headerComponent: H3,
    linkTitle: 'Все материалы',
    tabs: [],
    itemWidthMobile: '80%',
  };

  state = {
    activeTab: 0,
  };

  get linkPosition() {
    const { tabs, linkPosition } = this.props;
    return (tabs.length > 0) && linkPosition === 'top' ? 'bottom-left' : linkPosition;
  }

  get isHeaderNeeded() {
    const { tabs, title } = this.props;
    return Boolean(tabs.length || isNotEmptyReactElementContent(title));
  }

  render() {
    const {
      title,
      headerComponent: Header,
      tabs,
      link,
      isMobile,
      linkTitle,
      linkPosition,
      // eslint-disable-next-line no-unused-vars
      breakpoint,
      dataQa,
      content,
      ...baseDrumProps
    } = this.props;

    if (!content?.length) return null;

    const { activeTab } = this.state;
    const { theme, spaceBetween, headerIndent } = baseDrumProps;

    const Wrapper = isMobile ? Scroller : EmptyWrapper;

    // TODO: Кажется, функционал ссылок не используется, а поддерживать его
    // приходится, рассмотреть возможность его выпиливания
    const linkEl = link && linkPosition && !isMobile && (
      <div
        className={cx(
          'link',
          this.linkPosition === 'top' && styles.linkTop,
          this.linkPosition !== 'top' && 'linkBottom',
          this.linkPosition === 'bottom-center' && styles.linkCenter
        )}
        {...dataQa && { 'data-qa': dataQa + '-link' }}
      >
        <style jsx>{`
          .link
            font ${theme.controls.drum.link.font}
          .linkBottom
            margin-top ${theme.controls.drum.const.bottomSpacing + 'px'}
        `}</style>
        <Link type='tertiary' to={link}>{linkTitle}</Link>
      </div>
    );

    return (
      <div>
        <style jsx>{`
          .${styles.header}
            margin-bottom ${typeof headerIndent === 'number' ? headerIndent : spaceBetween}px
        `}</style>
        {this.isHeaderNeeded && (
          <div className={styles.header}>
            {isNotEmptyReactElementContent(title) && (
              <Header {...dataQa && { 'data-qa': dataQa + '-title' }}>{title}</Header>
            )}
            {this.linkPosition === 'top' && linkEl}
            {tabs.length > 0 && (
              <Wrapper>
                <div className={styles.tabs} {...dataQa && { 'data-qa': dataQa + '-tabs' }}>
                  {tabs.map((item, index) => (
                    <Button
                      size='small'
                      type={activeTab === index ? 'secondary' : 'ghost'}
                      state={activeTab === index ? 'idle' : 'auto'}
                      inactive={index === activeTab}
                      key={index}
                      onClick={() => {this.setState({ activeTab: index });}}
                    >
                      {item.title}
                    </Button>
                  ))}
                </div>
              </Wrapper>
            )}
          </div>
        )}
        <BaseDrum
          content={content}
          {...baseDrumProps}
          link={link}
          dataQa={dataQa}
          linkTitle={linkTitle}
          {...tabs[activeTab]}
          activeTab={activeTab}
        />
        {this.linkPosition !== 'top' && linkEl}
      </div>
    );
  }
}

const DefaultDrum = withBreakpoint(withTheme(Drum));
DefaultDrum.displayName = 'Drum';

export default DefaultDrum;
export { BaseDrum };
export { Drum as StorybookComponent };
